import React, { useState } from 'react';

import { Document as PDFDocument, Page, pdfjs } from 'react-pdf';

import { Download, ExternalLink } from 'lucide-react';

import Loader from './Loader';
import OriginalFile from './OriginalFile';

// Set up PDF.js worker - use CDN fallback
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// CVE-2024-4367 (PAAS-1587): pdfjs-dist < 4.2.67 evals font glyph paths, so a
// crafted PDF runs script in the host origin unless eval is disabled.
const PDF_DOCUMENT_OPTIONS = { isEvalSupported: false };

interface Document {
	id: string;
	name: string;
	type: string;
	date: string;
	size: string;
	status: string;
	processingMonth: string;
	pdfUrl?: string;
	originalUrl?: string;
	originalType?: string;
	originalName?: string;
	pdfStatus?: 'ready' | 'preparing' | 'failed';
	pdfPollingStopped?: boolean;
	results?: any;
}

interface DocumentViewerProps {
	document: Document;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ document }) => {
	const [numPages, setNumPages] = useState<number | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [tab, setTab] = useState<'statement' | 'original'>('statement');
	// Until a readable PDF exists, open on the upload itself.
	const [tabChosen, setTabChosen] = useState(false);
	const activeTab = document.pdfUrl || tabChosen ? tab : 'original';
	const statementLabel = document.pdfUrl
		? 'Statement'
		: document.pdfStatus === 'preparing'
			? 'Statement (preparing…)'
			: document.pdfStatus === 'failed'
				? 'Statement (unavailable)'
				: 'Statement';

	const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
		setNumPages(numPages);
		setLoading(false);
		setError(false);
	};

	const onDocumentLoadError = () => {
		setError(true);
		setLoading(false);
	};

	return (
		<div className="h-full flex flex-col bg-gray-100">
			{document.originalUrl && (
				<div className="flex gap-1 px-3 pt-2 bg-white border-b border-gray-200">
					{(['statement', 'original'] as const).map((key) => (
						<button
							key={key}
							type="button"
							onClick={() => {
								setTab(key);
								setTabChosen(true);
							}}
							className={`px-3 py-1.5 text-sm rounded-t ${
								activeTab === key
									? 'bg-gray-100 font-medium text-gray-900'
									: 'text-gray-500 hover:text-gray-700'
							}`}
						>
							{key === 'statement' ? statementLabel : 'Original'}
						</button>
					))}
				</div>
			)}
			{activeTab === 'original' && document.originalUrl ? (
				<OriginalFile
					url={document.originalUrl}
					contentType={document.originalType}
					filename={document.originalName}
				/>
			) : (
				/* PDF Content */
				<div className="flex-1 overflow-hidden bg-gray-100">
					{document.pdfUrl && !error ? (
						<div className="h-full flex flex-col">
							{/* PDF Header */}
							{numPages && (
								<div className="flex items-center justify-between p-3 bg-white border-b border-gray-200">
									<div className="flex items-center space-x-2">
										<span className="text-sm text-gray-600">
											{numPages} page{numPages !== 1 ? 's' : ''}
										</span>
									</div>
									<a
										href={document.pdfUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
									>
										<Download className="w-4 h-4 mr-1" />
										Download
									</a>
								</div>
							)}

							{/* PDF Viewer with Scrolling */}
							<div className="flex-1 overflow-auto p-4">
								{loading && (
									<div className="flex items-center justify-center h-full">
										<Loader size="md" text="Loading statement..." />
									</div>
								)}

								<div className="flex flex-col items-center space-y-4">
									<PDFDocument
										file={document.pdfUrl}
										options={PDF_DOCUMENT_OPTIONS}
										onLoadSuccess={onDocumentLoadSuccess}
										onLoadError={onDocumentLoadError}
										loading={
											<div className="flex items-center justify-center h-64">
												<Loader size="md" text="Loading statement..." />
											</div>
										}
									>
										{Array.from(new Array(numPages), (el, index) => (
											<div key={`page_${index + 1}`} className="mb-4">
												<Page
													pageNumber={index + 1}
													width={Math.min(600, window.innerWidth * 0.8)}
													renderTextLayer={false}
													renderAnnotationLayer={false}
												/>
												{numPages && numPages > 1 && (
													<div className="text-center mt-2 text-sm text-gray-500">
														Page {index + 1} of {numPages}
													</div>
												)}
											</div>
										))}
									</PDFDocument>
								</div>
							</div>
						</div>
					) : (
						<div className="flex items-center justify-center h-full text-gray-500">
							<div className="text-center">
								{document.pdfUrl ? (
									<>
										<p className="text-lg font-semibold mb-2">
											PDF Preview Unavailable
										</p>
										<p className="text-sm mb-4">
											Unable to load PDF in browser.
										</p>
										<a
											href={document.pdfUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
										>
											<ExternalLink className="w-4 h-4 mr-2" />
											Open Statement in New Tab
										</a>
									</>
								) : document.pdfStatus === 'preparing' &&
								  document.pdfPollingStopped ? (
									<>
										<p className="text-lg font-semibold mb-2">
											Still preparing a readable copy
										</p>
										<p className="text-sm">
											Refresh the page in a minute. The Original tab shows the
											file as uploaded.
										</p>
									</>
								) : document.pdfStatus === 'preparing' ? (
									<Loader size="md" text="Preparing a readable copy..." />
								) : document.pdfStatus === 'failed' ? (
									<>
										<p className="text-lg font-semibold mb-2">
											We couldn't make a readable copy
										</p>
										<p className="text-sm">
											Open the Original tab to see the file as uploaded. We'll
											try again later.
										</p>
									</>
								) : (
									<>
										<p className="text-lg font-semibold mb-2">
											No statement file available
										</p>
										<p className="text-sm">
											The file for this statement is not available.
										</p>
									</>
								)}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default DocumentViewer;
